
import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from "@/components/ui/button";
import LocationHero from '@/components/locations/LocationHero';
import LocationFeatures from '@/components/locations/LocationFeatures';
import LocationTestimonials from '@/components/locations/LocationTestimonials';
import LocationCTA from '@/components/locations/LocationCTA';
import { Helmet } from 'react-helmet-async';

const LyngbyLocation = () => {
  const features = [
    {
      title: "Centralt i Lyngby",
      description: "Undervisning nær Lyngby Station med nem adgang til offentlig transport og indkøbsmuligheder.",
      icon: "location"
    },
    {
      title: "Fleksible tider",
      description: "Undervisningstider der kan tilpasses din hverdag, både eftermiddage, aftener og weekender.",
      icon: "clock"
    },
    {
      title: "Personlig undervisning",
      description: "Individuel tilgang til hver elev med fokus på dine musikalske interesser og mål.",
      icon: "music"
    },
    {
      title: "Konservatorieuddannet",
      description: "Undervisning med Oscar Gilbert, uddannet på Rytmisk Musikkonservatorium.",
      icon: "award"
    }
  ];

  const testimonials = [
    {
      name: "Charlotte Andersen",
      location: "Lyngby",
      text: "Oscar formår at gøre hver time inspirerende og lærerig. Hans entusiasme for musik er smittende, og jeg glæder mig altid til klavertimerne.",
      stars: 5
    },
    {
      name: "Mads Petersen",
      location: "Lyngby",
      text: "Min datter har fået en helt ny interesse for musik efter at være startet hos Oscar. Han har en fantastisk måde at formidle selv komplekse musikalske begreber på.",
      stars: 5
    },
    {
      name: "Lise Sørensen",
      location: "Virum",
      text: "Jeg har prøvet flere klaverlærere gennem tiden, men Oscar skiller sig ud ved sin kombination af professionalisme og imødekommenhed.",
      stars: 5
    }
  ];

  return (
    <Layout>
      <Helmet>
        <title>Klaverundervisning i Lyngby | Få en gratis prøvetime!</title>
        <meta name="description" content="Professionel klaverundervisning i Lyngby med Oscar Gilbert. Book en gratis prøvetime og start din musikalske rejse i dag!" />
      </Helmet>
      
      <LocationHero 
        title="Klaverundervisning i Lyngby"
        description="Få professionel klaverundervisning med Oscar Gilbert i Lyngby. Undervisning for både børn og voksne i inspirerende omgivelser tæt på Lyngby Station."
      />
      
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">
                Klaverundervisning i <span className="text-piano-gold">Lyngby</span>
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                I Lyngby tilbyder Oscar Gilbert klaverundervisning af høj kvalitet i behagelige og inspirerende omgivelser. Med fokus på elevens individuelle behov og interesser skaber Oscar et læringsmiljø, der fremmer både teknisk udvikling og musikalsk forståelse.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Undervisningen foregår på et kvalitetsklaver og tilpasses dit niveau og dine mål, uanset om du er begynder eller øvet, barn eller voksen.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                Med Oscars baggrund fra Rytmisk Musikkonservatorium og hans omfattende erfaring som både udøvende pianist og underviser får du en kompetent og engageret guide på din musikalske rejse.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="bg-piano-gold hover:bg-piano-gold/90 text-white">
                  <Link to="/kontakt">Book gratis prøvetime</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/undervisning">Se undervisningstilbud</Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-piano-gold to-amber-400 rounded-lg blur opacity-20"></div>
              <div className="relative bg-white p-3 rounded-lg shadow-xl">
                <div className="aspect-square bg-gray-100 rounded-md overflow-hidden">
                  <img 
                    src="/lovable-uploads/891ee3d3-fc7a-4f1b-925d-4c77de27f6ae.png" 
                    alt="Klaverundervisning i Lyngby" 
                    className="w-full h-full object-cover object-center object-[50%_35%]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <LocationFeatures features={features} />
      
      <LocationTestimonials testimonials={testimonials} locationName="Lyngby" />
      
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-serif font-bold mb-4">Find vej til undervisningen</h2>
              <p className="text-lg text-gray-600">
                Klaverundervisningen foregår på Niels Finsens Alle 86, 2860 Søborg, med nem adgang fra Lyngby.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-xl mb-10">
              <div className="overflow-hidden rounded-md">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12607.679143124358!2d12.514096630724325!3d55.734728901797794!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x465251f89eae19fd%3A0x1029a319978e54b7!2sNiels%20Finsens%20Alle%2086%2C%202860%20S%C3%B8borg!5e0!3m2!1sda!2sdk!4v1743668909093!5m2!1sda!2sdk" 
                  width="100%" 
                  height="450" 
                  style={{ border: 0 }} 
                  allowFullScreen={true} 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Lokation på kort">
                </iframe>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="text-xl font-serif font-semibold">Adresse</h3>
                  <p className="text-gray-700">
                    Niels Finsens Alle 86<br />
                    2860 Søborg
                  </p>
                  <div className="space-y-2">
                    <p className="text-gray-700"><strong>Transport fra Lyngby:</strong></p>
                    <p className="text-gray-700">• God forbindelse med bus og S-tog fra Lyngby</p>
                    <p className="text-gray-700">• Nem adgang med bil via Helsingørmotorvejen</p>
                    <p className="text-gray-700">• Gratis parkering ved undervisningslokalet</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl font-serif font-semibold">Kontakt</h3>
                  <p className="text-gray-700">
                    Telefon: <a href="tel:+4540817127" className="text-piano-gold hover:underline">+45 40 81 71 27</a>
                  </p>
                  <p className="text-gray-700">
                    Email: <a href="mailto:kontakt@pianoundervisning.dk" className="text-piano-gold hover:underline">kontakt@pianoundervisning.dk</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <LocationCTA locationName="Lyngby" />
    </Layout>
  );
};

export default LyngbyLocation;
